/**
 * Drive folders — flat list of the user's tracked folders across drive
 * providers, used by the chat composer FolderPicker to scope the next
 * query to a subset of indexed folders.
 *
 * BE source-of-truth:
 *   - ``ando-agent/src/api/v1/endpoints/drive_management.py`` ::
 *     ``GET /drive-management/folders`` (aggregator added May 20, 2026)
 *
 * The endpoint is white-label: ``provider`` is a capability-side enum
 * (``google_drive`` | ``onedrive`` | ``sharepoint``), never a vendor
 * brand in the URL path.
 *
 * v1 source-of-truth: per-provider ``/drive-management/tracked-folders``
 * was queried 3 times (once per provider) from QueryInput.tsx. The new
 * aggregator collapses those round-trips into one.
 */

import { apiClient } from "../ApiClient.js";

export type DriveFolderProvider = "google_drive" | "onedrive" | "sharepoint";

export interface DriveFolder {
  /**
   * Stable UUID of the ``drive_tracked_folders`` row. Use this as a
   * React key only — it is NOT what Weaviate indexes against. Sending
   * this on the ``folder_filter[]`` wire field silently produces 0
   * scoped chunks (Jun 8 2026 bug, fixed at the consumer call site).
   */
  readonly id: string;
  /**
   * Provider-native folder id (Google file_id / OneDrive item id /
   * SharePoint item id). **This is the value to send on
   * ``folder_filter[]``** — it's the same string the ingestion writes
   * into the Weaviate ``folder_id`` property
   * (``file_meta.parent_folder_id``).
   */
  readonly folder_id: string;
  /** Human-readable folder name, falls back to path. */
  readonly name: string;
  /** Full virtual path inside the drive ("/Vendite/Q2"). */
  readonly path: string;
  readonly provider: DriveFolderProvider;
}

interface DriveFoldersResponse {
  readonly folders: readonly DriveFolder[];
}

export interface ListDriveFoldersFilters {
  /** Restrict to one provider; default = all connected providers. */
  readonly provider?: DriveFolderProvider;
  /** Hard cap (BE defaults to 200, max 500). */
  readonly limit?: number;
}

/**
 * GET ``/drive-management/folders`` — flat list of folders the user has
 * already opted-in to track. Returns ``[]`` when no Drive is connected.
 */
export async function listDriveFolders(
  filters?: ListDriveFoldersFilters,
): Promise<readonly DriveFolder[]> {
  const params = new URLSearchParams();
  if (filters?.provider) params.set("provider", filters.provider);
  if (typeof filters?.limit === "number") {
    params.set("limit", String(filters.limit));
  }
  const qs = params.toString();
  const res = await apiClient.get<DriveFoldersResponse>(
    `/drive-management/folders${qs.length > 0 ? `?${qs}` : ""}`,
  );
  return res.folders;
}

// ─── Unified scope folders (June 8, 2026) ─────────────────────────────
//
// Drive folders ARE scope folders, but the composer SourceScopePicker
// also needs filesystem-desktop ``base_paths`` (Tauri NAS sync roots).
// The new endpoint aggregates both behind one wire shape so the picker
// stays single-source. The ``filter_key`` field is what rides on the
// WS ``folder_filter[]`` field — bare string for Drive, ``fs://<path>``
// for filesystem. The BE discovery service splits on the prefix.

export type ScopeFolderSource =
  | "google_drive"
  | "onedrive"
  | "sharepoint"
  | "filesystem";

export interface ScopeFolderRow {
  /** Synthetic stable key for React (not the wire value). */
  readonly id: string;
  /**
   * Value to push on the WS ``folder_filter[]`` envelope. Drive →
   * provider-native folder_id. Filesystem → ``fs://<abs_path>``.
   */
  readonly filter_key: string;
  /** Display label (last path segment or folder name). */
  readonly name: string;
  /** Full path inside the source (``/Acme/Q3`` or ``/Users/me/Docs``). */
  readonly path: string;
  readonly provider: ScopeFolderSource;
}

interface ScopeFoldersResponse {
  readonly folders: readonly ScopeFolderRow[];
}

/**
 * GET ``/drive-management/scope/folders`` — unified Drive + filesystem
 * scope rows. Empty array when the user has neither Drive connected nor
 * a filesystem connector configured.
 */
export async function listScopeFolders(
  signal?: AbortSignal,
): Promise<readonly ScopeFolderRow[]> {
  const res = await apiClient.get<ScopeFoldersResponse>(
    "/drive-management/scope/folders",
    signal ? { signal } : undefined,
  );
  return res.folders;
}
